import { Suspense, useEffect, useState } from 'react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import { useCreateOrUpdatePermission, usePermissionFormHandler } from './hooks';
import { PermissionFormElement } from './Styles/StyledComponents';
import { useGetPermissionContent } from '@/api/permission/permission';
import { PermissionContentTypeSchema } from '@/zod_schemas';
import AssignGroupPermission from './AssignGroupPermission';
import LoadingState from '@/components/LoadingState';

const AssignPermission = () => {
  const { handleSubmit, getValues, mode, permissionId } =
    usePermissionFormHandler();

  const { data, isLoading, error } = useGetPermissionContent();

  const [permissionGroups, setPermissionGroups] = useState<
    PermissionContentTypeSchema[]
  >([]);

  useEffect(() => {
    if (data && getValues) {
      const initialPermissions = data.map((group) => ({
        ...group,
        permissions: group.permissions.map((permission) => ({
          ...permission,
          allowed:
            mode !== 'create' &&
            getValues().permissions?.includes(permission.id),
        })),
      }));
      setPermissionGroups(initialPermissions);
    }
  }, [data, getValues, mode]);

  const onSubmit = useCreateOrUpdatePermission(
    mode,
    permissionGroups,
    permissionId,
  );

  if (isLoading) {
    return <LoadingState label="Loading permission users…" />;
  }

  if (error) {
    return <Alert severity="error">Failed to load permissions.</Alert>;
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack spacing={3}>
        <PermissionFormElement onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              position: 'sticky',
              top: 80, // Adjust based on header height
              bgcolor: 'background.paper',
              px: { xs: 1.5, md: 2 },
              py: 1.5,
              zIndex: 100,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h3">Assign users</Typography>
          </Box>
          <AssignGroupPermission permissionId={permissionId!} />
        </PermissionFormElement>
      </Stack>
    </Paper>
  );
};

const AssignPermissionWithSuspense = () => (
  <Suspense fallback={<LoadingState label="Loading user assignments…" />}>
    <AssignPermission />
  </Suspense>
);

export default AssignPermissionWithSuspense;
